/**
 * Accès à l'API. Le cookie de session est httpOnly : le navigateur le joint
 * seul ; on se contente de poser l'en-tête anti-CSRF `X-Publiqa` attendu par
 * l'API sur toute écriture.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message)
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T
  const body = (await res.json().catch(() => null)) as
    (T & { detail?: string; errors?: { field: string; message: string }[] }) | null
  if (!res.ok) {
    const fields: Record<string, string> = {}
    for (const e of body?.errors ?? []) fields[e.field] = e.message
    throw new ApiError(res.status, body?.detail ?? `Erreur ${res.status}`, fields)
  }
  return body as T
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('X-Publiqa', '1')
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  const res = await fetch(`/api/v1${path}`, { ...init, headers, credentials: 'same-origin' })
  return handle<T>(res)
}

export const get = <T>(path: string) => api<T>(path)
export const post = <T>(path: string, body?: unknown) =>
  api<T>(path, {
    method: 'POST',
    body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
  })
export const put = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) })
export const patch = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
export const del = <T = void>(path: string) => api<T>(path, { method: 'DELETE' })

/**
 * Envoi multipart avec progression — `fetch` ne sait pas la suivre, d'où XHR.
 * Utilisé pour les médias et les pièces jointes, qui peuvent peser plusieurs Mo.
 */
export function upload<T>(
  path: string,
  form: FormData,
  onProgress?: (pct: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `/api/v1${path}`)
    xhr.setRequestHeader('X-Publiqa', '1')
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100))
    }
    xhr.onload = () => {
      const res = new Response(xhr.responseText || null, {
        status: xhr.status,
        headers: { 'Content-Type': 'application/json' },
      })
      handle<T>(res).then(resolve, reject)
    }
    xhr.onerror = () => reject(new ApiError(0, 'Connexion impossible. Vérifiez votre réseau.'))
    xhr.send(form)
  })
}
